<template>
  <validation-observer
    ref="form"
    #default="{ invalid }"
  >
    <b-modal
      :id="modalName"
      size="lg"
      ok-only
      :ok-title="$t('save')"
      :ok-disabled="submitSpinner || invalid"
      no-close-on-backdrop
      @ok="onSubmit"
      @hidden="hideModal"
    >
      <template #modal-title="{}">
        <span>
          {{ isUpdated ? $t("Update") : $t("Create") }}
        </span>
        <span> {{ $t("Service") }}</span>
        <span v-if="isUpdated">{{ form.package.name }}</span>
        <span v-if="isUpdated">{{ form.user.name }}</span>
      </template>
      <b-form>
        <b-row>
          <b-col
            v-if="$can('create','services') && !isUpdated"
            cols="12"
            md="6"
          >
            <b-form-group :label="$t('User')">
              <validation-provider
                #default="{ errors }"
                :name="$t('User')"
                vid="user_id"
                rules="required"
              >
                <select-user
                  :label="$t('User')"
                  :value="form.user_id"
                  name="user_id"
                  :state="errors.length > 0 ? false : null"
                  @onModelChange="(name,value) => form.user_id = value"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col
            md="6"
            cols="12"
          >
            <b-form-group :label="$t('Service')">
              <validation-provider
                #default="{ errors }"
                :name="$t('Service')"
                vid="package_id"
                rules="required"
              >
                <v-select
                  v-model="form.package_id"
                  :options="additionalData.packages"
                  :clearable="false"
                  label="name"
                  :reduce="_package => _package.id"
                  :state="errors.length > 0 ? false : null"
                  @input="packageChange"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col
            md="6"
            cols="12"
          >
            <b-form-group
              :label="$t('Limit per day')"
            >
              <validation-provider
                #default="{ errors }"
                :name="$t('Limit per day')"
                vid="limit_per_day"
                rules="required"
              >
                <b-form-input
                  id="limit_per_day"
                  v-model="form.limit_per_day"
                  type="text"
                  :state="errors.length > 0 ? false : null"
                  :placeholder="$t('Limit per day')"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col
            md="6"
            cols="12"
          >
            <b-form-group
              :label="$t('Number of accounts')"
            >
              <validation-provider
                #default="{ errors }"
                :name="$t('Number of accounts')"
                vid="number_of_accounts"
                rules="required"
              >
                <b-form-input
                  id="number_of_accounts"
                  v-model="form.number_of_accounts"
                  type="text"
                  :state="errors.length > 0 ? false : null"
                  :placeholder="$t('Number of accounts')"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col
            md="6"
            cols="12"
          >
            <b-form-group
              :label="$t('Webhook')"
            >
              <validation-provider
                #default="{ errors }"
                :name="$t('Webhook')"
                vid="webhook"
                rules="required"
              >
                <b-form-input
                  id="webhook"
                  v-model="form.webhook"
                  type="text"
                  :state="errors.length > 0 ? false : null"
                  :placeholder="$t('Webhook')"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col
            md="6"
            cols="12"
          >
            <b-form-group
              :label="$t('Messages count')"
            >
              <validation-provider
                #default="{ errors }"
                :name="$t('Messages count')"
                vid="messages_count"
                rules="required"
              >
                <b-form-input
                  id="messages_count"
                  v-model="form.messages_count"
                  type="text"
                  :state="errors.length > 0 ? false : null"
                  :placeholder="$t('Messages count')"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col
            md="6"
            cols="12"
          >
            <b-form-group
              :label="$t('Extra messages')"
            >
              <validation-provider
                #default="{ errors }"
                :name="$t('Extra messages')"
                vid="extra_messages"
                rules="required"
              >
                <b-form-input
                  id="extra_messages"
                  v-model="form.extra_messages"
                  type="text"
                  :state="errors.length > 0 ? false : null"
                  :placeholder="$t('Extra messages')"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col
            md="6"
            cols="12"
          >
            <b-form-group :label="$t('Status')">
              <validation-provider
                #default="{ errors }"
                :name="$t('Status')"
                vid="status"
                rules="required"
              >
                <v-select
                  v-model="form.status"
                  :options="additionalData.statuses"
                  :clearable="false"
                  label="label"
                  :reduce="status => status.key"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col
            md="6"
            cols="12"
          >
            <b-form-group
              :label="$t('Expire at')"
            >
              <validation-provider
                #default="{ errors }"
                :name="$t('Expire at')"
                vid="expire_at"
              >
                <date-picker
                  id="expire_at"
                  v-model="form.expire_at"
                  type="datetime"
                  format="YYYY-MM-DD HH:mm"
                  display-format="jYYYY/jMM/jDD HH:mm"
                  color="#28c76f"
                  :state="errors.length > 0 ? false : null"
                  :placeholder="$t('Expire at')"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
        </b-row>

      </b-form>
      <template #modal-ok="{}">
        <span>
          <b-spinner
            v-if="submitSpinner"
            small
          />
          {{ $t("save") }}
        </span>
      </template>
    </b-modal>
  </validation-observer>
</template>

<script>
import { EditFormLayoutMixin, EditModalMixin } from '../../../common/mixins/form'
import SelectUser from '../../core/edit/SelectUser.vue'

export default {
  name: 'ServicesEditModal',
  components: {
    SelectUser,

  },
  mixins: [EditFormLayoutMixin, EditModalMixin],
  data() {
    return {
      routeName: 'services',
      modalName: 'modal-services-edit',
      createAbleFields: [
        'user_id',
        'package_id',
        'limit_per_day',
        'number_of_accounts',
        'webhook',
        'messages_count',
        'extra_messages',
        'status',
        'expire_at',
      ],
    }
  },
  methods: {
    packageChange(packageId) {
      if (this.isUpdated) return
      const packageValue = this.additionalData.packages.find(value => value.id.toString() === packageId.toString())
      const { panel } = packageValue
      this.form.limit_per_day = panel.limit_per_day
      this.form.number_of_accounts = panel.number_of_accounts
      this.form.webhook = panel.webhook
      this.form.messages_count = panel.messages_count
      this.form.extra_messages = panel.extra_messages
      this.form.expire_at = packageValue.expire_at
      this.form.extra_messages = 0
    },
  },
}
</script>

<style scoped>

</style>
